import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title: string;
  description: string;
  type?: 'website' | 'article' | 'profile';
  image?: string;
  schema?: object;
  canonical?: string;
}

export function SEO({ 
  title, 
  description, 
  type = 'website', 
  image = '/logo-libraddict.png',
  schema,
  canonical
}: SEOProps) {
  const fullTitle = `${title} | Libr'Addict`;
  const siteUrl = window.location.origin;
  const currentUrl = canonical || window.location.href;
  const imageUrl = image.startsWith('http') ? image : `${siteUrl}${image}`;
  
  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      <link rel="canonical" href={currentUrl} />
      
      <meta property="og:type" content={type} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:site_name" content="Libr'Addict" />
      <meta property="og:locale" content="fr_FR" />
      
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={currentUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />
      
      {schema && (
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      )}
    </Helmet>
  );
}

export const schemas = {
  organization: {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: "Libr'Addict",
    description: "Ressources et soutien en addictologie — anonyme, gratuit, sans jugement",
    url: window.location.origin,
    logo: `${window.location.origin}/logo-libraddict.png`,
    sameAs: [],
  },
  
  searchAction: {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    url: window.location.origin,
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${window.location.origin}/resources?q={search_term_string}`
      },
      'query-input': 'required name=search_term_string'
    }
  },

  faqPage: (questions: Array<{ question: string; answer: string }>) => ({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: questions.map(q => ({
      '@type': 'Question',
      name: q.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: q.answer
      }
    }))
  }),

  medicalBusiness: (practitioner: { name: string; specialty: string; location: string }) => ({
    '@context': 'https://schema.org',
    '@type': 'MedicalBusiness',
    name: practitioner.name,
    medicalSpecialty: practitioner.specialty,
    address: {
      '@type': 'PostalAddress',
      addressLocality: practitioner.location,
      addressCountry: 'FR'
    }
  })
};
